'use client';

import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';
import { preconnect } from 'react-dom';
import dynamic from 'next/dynamic';
import { useTranslation } from 'react-i18next';
import { autoTopupEnabled, embeddedCheckoutEnabled, firstCheckoutSessionBody } from '@/lib/credit-packages';
import {
  createEmbeddedCheckoutSession,
  type EmbeddedCheckoutSession,
  type PurchaseRequestBody,
  startCheckout,
} from '@/lib/stripe-client';

/**
 * Every credit purchase (plans page packages and any-amount top-up, the
 * wallet popover, the insufficient-credits dialog, `?buy=` after signup)
 * goes through `openCheckout`.
 *
 * - Flag off (the default): the hosted Stripe Checkout redirect,
 *   exactly as before — `startCheckout` with the caller's body.
 * - NEXT_PUBLIC_STRIPE_EMBEDDED_CHECKOUT=1 (prod and dev): the in-page checkout
 *   modal: amount cards, the auto top-up switch (only with
 *   NEXT_PUBLIC_AUTO_TOPUP=1) and Stripe Elements on our page.
 *
 * Resolves to 'redirect' (keep the button's spinner, the page is leaving)
 * or 'modal' (the modal is open, clear it).
 */
type OpenCheckout = (body: PurchaseRequestBody) => Promise<'redirect' | 'modal'>;

const EMBEDDED = embeddedCheckoutEnabled();
const AUTO_TOPUP = autoTopupEnabled();

// The modal's chunk, loaded once (and again after a failed load). Importing
// it starts Stripe.js: @stripe/stripe-js injects its script on import.
let dialogModule: Promise<typeof import('./CheckoutDialog')> | null = null;
function loadDialog() {
  dialogModule ??= import('./CheckoutDialog').catch((error: unknown) => {
    dialogModule = null;
    throw error;
  });
  return dialogModule;
}

// Stripe.js and the modal load only when the flag is on, on a purchase page
// or a buy intent (usePreloadCheckout) or when a purchase opens.
const CheckoutDialog = EMBEDDED ? dynamic(() => loadDialog().then((m) => m.CheckoutDialog), { ssr: false }) : null;

const redirect: OpenCheckout = async (body) => {
  await startCheckout(body);
  return 'redirect';
};

const CheckoutContext = createContext<OpenCheckout>(redirect);
const PreloadContext = createContext<() => void>(() => {});

/** Only a real publishable key starts Stripe.js early (an unset env falls
 *  back to the key the session answer carries). */
const validKey = (key: string | null | undefined) => (key && /^pk_(live|test)_/.test(key) ? key : null);

export function CheckoutProvider({
  children,
  publishableKey,
}: {
  children: ReactNode;
  /** STRIPE_PUBLISHABLE_KEY from the root layout (flag on only), so Stripe.js
   *  can start before the session answers. */
  publishableKey?: string | null;
}) {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('de') ? 'de' : 'en';
  const earlyKey = validKey(publishableKey);

  // Warm up the modal: its chunk, Stripe.js and the Stripe instance (which
  // boots Stripe's controller frame), so opening only waits for the session.
  const preload = useCallback(() => {
    if (!EMBEDDED) return;
    preconnect('https://js.stripe.com');
    void loadDialog()
      .then((m) => (earlyKey ? m.warmStripe(earlyKey, locale) : null))
      .catch(() => {
        // Opening the modal tries again and shows the failure.
      });
  }, [earlyKey, locale]);

  // The purchase the modal opened with and the session asked for on open;
  // a new object per open remounts it.
  const [request, setRequest] = useState<{
    body: PurchaseRequestBody;
    id: number;
    session: Promise<EmbeddedCheckoutSession>;
  } | null>(null);
  const openModal = useCallback<OpenCheckout>(
    async (body) => {
      preload();
      // The session starts now, alongside the modal's chunk and Stripe.js,
      // instead of after them; the modal takes it over (and shows a failure).
      const session = createEmbeddedCheckoutSession(firstCheckoutSessionBody(body, AUTO_TOPUP));
      session.catch(() => {});
      setRequest({ body, id: Date.now(), session });
      return 'modal';
    },
    [preload],
  );
  const open = EMBEDDED ? openModal : redirect;
  const close = useCallback(() => setRequest(null), []);

  return (
    <CheckoutContext.Provider value={open}>
      <PreloadContext.Provider value={preload}>
        {children}
        {CheckoutDialog && request && (
          <CheckoutDialog
            key={request.id}
            initial={request.body}
            firstSession={request.session}
            publishableKey={earlyKey}
            onClose={close}
          />
        )}
      </PreloadContext.Provider>
    </CheckoutContext.Provider>
  );
}

/** Start a purchase: the modal (flag on) or the hosted redirect. */
export function useCheckout(): OpenCheckout {
  return useContext(CheckoutContext);
}

/** Warm up the checkout modal ahead of a likely purchase (a purchase page,
 *  hovering or focusing a buy button). A no-op without the flag; cheap to
 *  call repeatedly. */
export function usePreloadCheckout(): () => void {
  return useContext(PreloadContext);
}

/** Whether purchases open in the modal — for copy and links that differ. */
export const EMBEDDED_CHECKOUT = EMBEDDED;
